import React, { useEffect, useState, useMemo } from 'react';
import {
  Button, Table, Badge, Spinner, Card, Row, Col,
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import Error from '../../Components/Error';
import ExperienceInputModal from './Components/ExperienceInputModal';
import ExperienceGroupInputModal from './Components/ExperienceGroupInputModal';
import useModalState from '../../Hooks/useModalState';
import ExperienceService from '../../Services/ExperienceService';
import ExperienceGroupService from '../../Services/ExperienceGroupService';

function Experiences() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupId = searchParams.get('group');

  const [experiences, setExperiences] = useState([]);
  const [experienceGroups, setExperienceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [error, setError] = useState('');

  const experienceInputModal = useModalState();
  const groupInputModal = useModalState();

  // Get currently selected group object
  const selectedGroup = useMemo(
    () => experienceGroups.find((g) => g.Id === selectedGroupId),
    [experienceGroups, selectedGroupId],
  );

  // Filter experiences by selected group
  const filteredExperiences = useMemo(
    () => (selectedGroupId
      ? experiences.filter((exp) => exp.group_id === selectedGroupId)
      : experiences),
    [experiences, selectedGroupId],
  );

  // Navigate to a group
  const selectGroup = (groupId) => {
    setSearchParams({ group: groupId });
  };

  // Navigate back to groups view
  const clearGroupSelection = () => {
    setSearchParams({});
  };

  const fetchExperiences = async () => {
    setLoading(true);
    try {
      const response = await ExperienceService.fetchExperiences();
      setExperiences(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load experiences');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    setGroupsLoading(true);
    try {
      const response = await ExperienceGroupService.fetchExperienceGroups();
      setExperienceGroups(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load experience groups');
    } finally {
      setGroupsLoading(false);
    }
  };

  const debouncedFetch = _.debounce(() => {
    fetchExperiences();
    fetchGroups();
  }, 500);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, []);

  const handleDelete = async (experience) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to delete "${experience.name}"?`)) {
      return;
    }

    try {
      await ExperienceService.deleteExperience(experience.Id);
      fetchExperiences();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete experience');
    }
  };

  const handleDeleteGroup = async (group) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to delete "${group.title}"?`)) {
      return;
    }

    try {
      await ExperienceGroupService.deleteExperienceGroup(group.Id);
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete experience group');
    }
  };

  // Count experiences per group
  const getExperienceCount = (groupId) => (
    experiences.filter((exp) => exp.group_id === groupId).length
  );

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              {selectedGroupId ? (
                <>
                  <Button
                    variant="link"
                    className="p-0 me-2 text-decoration-none"
                    onClick={clearGroupSelection}
                    title="Back to groups"
                  >
                    <i className="fa fa-arrow-left text-primary" />
                  </Button>
                  {selectedGroup?.title || 'Experience Group'}
                </>
              ) : (
                <>
                  Experience
                  {' '}
                  <span className="hero-highlight">Groups</span>
                </>
              )}
            </h1>
            <p className="lead text-muted mb-4">
              {selectedGroupId
                ? 'Manage experiences in this group'
                : 'Manage pre-defined experiences for tours, activities, and guided services'}
            </p>
            <div className="hero-actions d-flex gap-2">
              {selectedGroupId ? (
                <Button
                  onClick={() => experienceInputModal.show({ group_id: selectedGroupId })}
                  size="lg"
                  variant="primary"
                >
                  <i className="fa fa-plus me-2" />
                  Create Experience
                </Button>
              ) : (
                <Button
                  onClick={() => groupInputModal.show({})}
                  size="lg"
                  variant="primary"
                >
                  <i className="fa fa-folder-plus me-2" />
                  Create Group
                </Button>
              )}
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className={`fa ${selectedGroupId ? 'fa-map-marked-alt' : 'fa-layer-group'} fa-5x text-primary opacity-25`} />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      {/* Conditional Rendering: Groups View or Experiences View */}
      {!selectedGroupId ? (
        /* Experience Groups View */
        <div>
          {groupsLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Loading groups...</p>
            </div>
          )}
          {!groupsLoading && experienceGroups.length === 0 && (
            <div className="text-center py-5">
              <i className="fa fa-folder-open fa-3x text-muted mb-3 d-block opacity-50" />
              <p className="text-muted">No experience groups found. Create your first group.</p>
            </div>
          )}
          {!groupsLoading && experienceGroups.length > 0 && (
            <Row xs={1} md={2} lg={3} xl={4} className="g-4">
              {experienceGroups.map((group) => (
                <Col key={group.Id}>
                  <Card
                    className="h-100 shadow-sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => selectGroup(group.Id)}
                  >
                    {group.image_url ? (
                      <Card.Img
                        variant="top"
                        src={group.image_url}
                        style={{ height: '140px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="bg-light d-flex align-items-center justify-content-center"
                        style={{ height: '140px' }}
                      >
                        <i className="fa fa-image fa-3x text-muted opacity-50" />
                      </div>
                    )}
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Card.Title className="mb-0 h6">{group.title}</Card.Title>
                        {group.featured === 1 && (
                          <Badge bg="warning" text="dark" className="ms-2">
                            <i className="fa fa-star" />
                          </Badge>
                        )}
                      </div>
                      {group.category && (
                        <Badge bg="secondary" className="mb-2">{group.category}</Badge>
                      )}
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-muted">
                          <i className="fa fa-map-marked-alt me-1" />
                          {getExperienceCount(group.Id)}
                          {' '}
                          experience(s)
                        </small>
                        {group.active !== 1 && (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </div>
                    </Card.Body>
                    <Card.Footer className="bg-transparent border-top-0 pt-0">
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            groupInputModal.show(group);
                          }}
                          title="Edit group"
                        >
                          <i className="fa fa-edit" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(group);
                          }}
                          title="Delete group"
                        >
                          <i className="fa fa-trash" />
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      ) : (
        /* Experiences View (filtered by group) */
        <div>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Loading experiences...</p>
            </div>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Duration</th>
                  <th>Activities</th>
                  <th>Status</th>
                  <th style={{ width: '180px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExperiences.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      <i className="fa fa-inbox fa-2x mb-2 d-block opacity-50" />
                      No experiences in this group. Create your first experience.
                    </td>
                  </tr>
                ) : (
                  filteredExperiences.map((exp) => (
                    <tr key={exp.Id}>
                      <td>
                        <strong>{exp.name}</strong>
                        {exp.description && (
                          <small className="text-muted d-block text-truncate" style={{ maxWidth: '250px' }}>
                            {exp.description}
                          </small>
                        )}
                      </td>
                      <td>
                        {exp.duration_hours}
                        h
                      </td>
                      <td>
                        <Badge bg="light" text="dark">
                          {exp.itinerary_template?.length || 0}
                        </Badge>
                      </td>
                      <td>
                        {exp.active === 1 ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => experienceInputModal.show(exp)}
                          >
                            <i className="fa fa-edit me-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              // Clone experience - remove Id so it creates a new one
                              const cloned = { ...exp, name: `${exp.name} (Copy)` };
                              delete cloned.Id;
                              experienceInputModal.show(cloned);
                            }}
                            title="Clone this experience"
                          >
                            <i className="fa fa-clone" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(exp)}
                          >
                            <i className="fa fa-trash" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {experienceInputModal.isVisible && (
        <ExperienceInputModal
          originalExperience={experienceInputModal.selectedObject}
          show={experienceInputModal.isVisible}
          fetchExperiences={fetchExperiences}
          handleClose={experienceInputModal.hide}
        />
      )}

      {groupInputModal.isVisible && (
        <ExperienceGroupInputModal
          originalGroup={groupInputModal.selectedObject}
          show={groupInputModal.isVisible}
          fetchGroups={fetchGroups}
          handleClose={groupInputModal.hide}
        />
      )}
    </>
  );
}

export default Experiences;
